import { PageHeader } from '@/components/PageHeader/PageHeader.tsx'
import { PageTextSearchFilter } from '@/components/PageSearchFilter/PageTextSearchFilter.tsx'
import { MarketplaceOverview } from '@/features/mocked/marketplace/components/MarketplaceOverview'
import { FEATURES, isFeatureEnabled } from '@/utils/feature-flag.ts'
import DashboardIcon from '@mui/icons-material/Dashboard'
import FilterListIcon from '@mui/icons-material/FilterList'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined'
import { Box, Button, Tab, Tabs } from '@mui/material'
import { ComingSoonChip } from '@/components'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_authenticated/$companySlug/marketplace/')({
  beforeLoad: () => {
    if (!isFeatureEnabled(FEATURES.MARKETPLACE)) {
      throw redirect({
        to: '/not-found'
      })
    }
  },
  component: Marketplace
})

function a11yProps(index: number) {
  return {
    id: `marketplace-tab-${index}`,
    'aria-controls': `marketplace-tabpanel-${index}`
  }
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`marketplace-tabpanel-${index}`}
      aria-labelledby={`marketplace-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </Box>
  )
}

function Marketplace() {
  const { t } = useTranslation('@feature_marketplace')
  const [tabValue, setTabValue] = useState(0)
  const [searchQuery, setSearch] = useState('')

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <PageHeader>{t('components.MarketplacePage.title')}</PageHeader>
        <ComingSoonChip label={t('components.MarketplacePage.comingSoon')} />
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label={t('components.MarketplacePage.tabs.ariaLabel')}
        >
          <Tab
            icon={<DashboardIcon />}
            iconPosition="start"
            label={t('components.MarketplacePage.tabs.overview')}
            {...a11yProps(0)}
          />
          <Tab
            icon={<WidgetsOutlinedIcon />}
            iconPosition="start"
            label={t('components.MarketplacePage.tabs.allServices')}
            disabled
            {...a11yProps(1)}
          />
          <Tab
            icon={<PersonOutlineIcon />}
            iconPosition="start"
            label={t('components.MarketplacePage.tabs.myServices')}
            disabled
            {...a11yProps(2)}
          />
        </Tabs>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Box sx={{ '& .MuiTextField-root': { width: 400 } }}>
          <PageTextSearchFilter
            placeholder={t('components.MarketplacePage.search')}
            value={searchQuery}
            onChange={setSearch}
          />
        </Box>
        <Button variant="outlined" startIcon={<FilterListIcon />}>
          {t('components.MarketplacePage.filter')}
        </Button>
      </Box>

      <CustomTabPanel value={tabValue} index={0}>
        <MarketplaceOverview />
      </CustomTabPanel>
    </>
  )
}
